import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    Typography,
    Chip,
    Box
} from '@mui/material';
import { ExpandMore, Warning, Error } from '@mui/icons-material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

import { ErrorProps, severityTable } from '../../models/Errors';
/**
 * Reusable error summary component with expandable details
 * 
 * @param {Object} props
 * @param {Array} props.errors - Array of error objects
 * @param {string} props.title - Custom title
 * @param {boolean} props.expanded - Initially expanded state
 * @param {function} props.onExpand - Callback when expansion changes (optional)
 * @param {string} props.severity - 'error' | 'warning' | 'info' (optional)
 */



const ErrorSummary: React.FC<ErrorProps> = ({
    errors = [],
    title,
    defaultExpanded = false,
    onExpand,
    severity = 'error'
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    if (!errors || errors.length === 0) return null;

    

    const severityConfig: severityTable = {
        error: { color: 'error', icon: <ErrorIcon />, defaultTitle: 'Errors occurred' },
        warning: { color: 'warning', icon: <WarningIcon />, defaultTitle: 'Warnings' },
        info: { color: 'info', icon: <InfoIcon />, defaultTitle: 'Information' }
    };

    const config = severityConfig[severity];

    const handleExpansionChange = () => {
        setExpanded(prev => !prev);
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={handleExpansionChange}
            sx={{
                mb: 2,
                border: 1,
                borderColor: `${severity}.light`,
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {config.icon}
                    <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'medium' }}>
                        {title || config.defaultTitle}
                    </Typography>
                </Box>
                <Chip
                    label={errors.length}
                    color={config.color}
                    size="small"
                    sx={{ ml: 2 }}
                />
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {errors.map((error, index) => (
                        <Alert
                            key={index}
                            severity={severity}
                            sx={{ width: '100%' }}
                            icon={false}
                        >
                            <Typography variant="body2" component="div">
                                {error.message || 'An unknown error occurred'}
                                {error.details && (
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        sx={{ mt: 0.5, color: 'text.secondary' }}
                                    >
                                        {error.details}
                                    </Typography>
                                )}
                                {error.code && (
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        Error code: {error.code}
                                    </Typography>
                                )}
                            </Typography>
                        </Alert>
                    ))}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default ErrorSummary;